import {
  BuildingIcon,
  ChartLineUpIcon,
  ChatIcon,
  ClipboardTextIcon,
  FileIcon,
  ListChecksIcon,
  PencilSimpleLineIcon,
  UsersThreeIcon
} from '@phosphor-icons/react';
import { pamSubmissionPath } from 'src/features/pam/urls';
import { formatTimestamp } from 'src/features/chat/utils';
import { useEffect, useState } from 'react';
import { apiGet } from 'src/lib/api/axios';
import DocumentTable, { Document } from 'src/features/home/components/DocumentTable';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { useOpenAction } from 'src/features/groups/components/ActionViewer';
import Loading from 'src/components/ui/Loading';

// Format title consistently (remove extension, underscores to spaces, capitalize)
const formatTitle = (title: string) => {
  return title
    .replace(/\.[^/.]+$/, '') // remove extension
    .replace(/_/g, ' ') // underscores to spaces
    .replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize each word
};

const formatSharedByName = (user: { firstname?: string | null; surname?: string | null }) => {
  const name = [user.firstname, user.surname].filter(Boolean).join(' ').trim();
  return name || 'Shared user';
};

function SharedResources({ variant = 'table' }: { variant?: 'table' | 'list' }) {
  const [resourcesList, setResourcesList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const navigate = useTenantNavigate();
  const openAction = useOpenAction();

  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const response = await apiGet('/permissions/shared_resources');
        setResourcesList(response.data);
      } catch (error) {
        console.error('Error fetching shared resources:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  const handleResourceRowClick = (doc: Document) => {
    const resource = resourcesList.find((r) => r.id === doc.id);
    if (!resource) return;

    const { type, id } = resource;
    if (type === 'chat') {
      navigate(`/chat/${id}`);
    } else if (type === 'review') {
      navigate(`/review/${id}`);
    } else if (type === 'dashboard') {
      navigate(`/reporting/dashboard/${id}`);
    } else if (type === 'writing') {
      navigate(`/write/${id}`);
    } else if (type === 'action') {
      openAction?.(id);
    } else if (type === 'document') {
      navigate(`/wiki/${id}`);
    } else if (type === 'pam_submission') {
      navigate(pamSubmissionPath(id));
    } else if (type === 'team_group') {
      navigate(`/groups/${id}`);
    }
  };

  // Transform resources to match Document interface
  const transformedDocuments = resourcesList.map((resource) => ({
    icon: (() => {
      switch (resource.type) {
        case 'chat':
          return <ChatIcon size={20} weight="light" />;
        case 'review':
          return <ClipboardTextIcon size={20} weight="light" />;
        case 'writing':
          return <PencilSimpleLineIcon size={20} weight="light" />;
        case 'document':
          return <FileIcon size={20} weight="light" />;
        case 'dashboard':
          return <ChartLineUpIcon size={20} weight="light" />;
        case 'action':
          return <ListChecksIcon size={20} weight="light" />;
        case 'pam_submission':
          return <BuildingIcon size={20} weight="light" />;
        case 'team_group':
          return <UsersThreeIcon size={20} weight="light" />;
        default:
          return <FileIcon size={20} weight="light" />;
      }
    })(),
    id: resource.id,
    title: formatTitle(resource.name),
    tags: [],
    lastViewed: resource.shared_at || '',
    published: ''
  }));

  const resourceColumns = [
    {
      key: 'title',
      header: 'Resource Name'
    },
    {
      key: 'type',
      header: 'Type',
      render: (doc: Document) => {
        const resource = resourcesList.find((r) => r.id === doc.id);
        const value = resource?.type;
        const labels: Record<string, string> = {
          pam_submission: 'PAM Submission',
          team_group: 'Group'
        };
        return <span className="capitalize">{labels[value] ?? value ?? 'Unknown'}</span>;
      }
    },
    {
      key: 'shared_by',
      header: 'Shared By',
      render: (doc: Document) => {
        const resource = resourcesList.find((r) => r.id === doc.id);
        const value = resource?.shared_by;
        if (!value) return <span className="text-gray-500">Unknown</span>;

        return (
          <div className="flex gap-2">
            <span className="">{formatSharedByName(value)}</span>
            <span className="text-sm text-content-subtlest">{value.organisation}</span>
          </div>
        );
      }
    },
    {
      key: 'shared_at',
      header: 'Shared At',
      render: (doc: Document) => {
        const resource = resourcesList.find((r) => r.id === doc.id);
        const value = resource?.shared_at;
        if (!value) return <span className="text-gray-500">Unknown</span>;

        try {
          return <div>{formatTimestamp(String(value))}</div>;
        } catch (error) {
          return <span className="text-gray-500">Invalid date</span>;
        }
      }
    }
  ];

  if (variant === 'list') {
    if (loading) {
      return <p className="text-sm text-content-subtle">Loading shared resources...</p>;
    }
    if (resourcesList.length === 0) {
      return <p className="text-sm text-content-subtle">No items have been shared with you yet.</p>;
    }
    return (
      <ul className="m-0 flex list-none flex-col divide-y divide-border-subtle p-0">
        {transformedDocuments.map((doc) => {
          const resource = resourcesList.find((item) => item.id === doc.id);
          const sharedBy = resource?.shared_by ? formatSharedByName(resource.shared_by) : null;
          return (
            <li key={doc.id}>
              <button
                type="button"
                onClick={() => handleResourceRowClick(doc)}
                className="flex w-full min-w-0 items-center gap-2 py-2 text-left hover:bg-surface-hover/10"
              >
                <span className="shrink-0 text-content-subtle">{doc.icon}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-content-default">{doc.title}</span>
                <span className="flex shrink-0 items-center gap-2 text-xs text-content-subtlest">
                  {sharedBy ? <span className="max-w-32 truncate">{sharedBy}</span> : null}
                  {doc.lastViewed ? (
                    <time dateTime={String(doc.lastViewed)} className="whitespace-nowrap">
                      {formatTimestamp(String(doc.lastViewed))}
                    </time>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8 h-72">
        <Loading text="Loading shared resources..." />
      </div>
    );
  }

  if (!resourcesList || resourcesList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 rounded-xl border-2 border-bluegray-300 text-content-subtle">
        <FileIcon className="w-12 h-12 mb-4" />
        <p className="mb-4">No items have been shared with you yet</p>
      </div>
    );
  }

  return (
    <DocumentTable
      documents={transformedDocuments}
      onRowClick={handleResourceRowClick}
      placeholder="Search shared resources..."
      columns={resourceColumns}
    />
  );
}

export default SharedResources;
